import { DataExplorationChart } from "reducers/configurableChartReducer";
import { v4 as createUUID } from "uuid";
import { getSeriesTitle } from "reducers/chartMenuReducer";
import {
  LineSeries,
  RangeSeries,
  ScadaMedianDataSource,
  ScadaRangeDataSource
} from "interfaces/chart";

/** How many charts are allowed on the page */
export const CHART_LIMIT = 10;

/** A completely blank chart with nothing in it */
const DEFAULT_CHART: DataExplorationChart = {
  version: "0.0.1",
  series: [],
  xAxes: [],
  yAxes: [],
  title: "",
  plotLines: []
};

/** Generates a chart and adds placeholder series, x-axis and y-axis */
export const generateNewChart = (
  id: string | null = null,
  machineId: number | null = null,
  machineName: string | null = null,
  tagId: number | null = null,
  tagName: string | null = null
): DataExplorationChart => {
  const newChart = { ...DEFAULT_CHART };
  const yAxisId = createUUID();
  const xAxisId = createUUID();

  newChart.series = [
    {
      id: id ?? createUUID(),
      title: getSeriesTitle(tagName, machineName),
      colorIndex: 0,
      seriesType: "line",
      pointsEnabled: true,
      xAxisId,
      yAxisId,
      dataSource: {
        type: "scada-datetime-tag",
        args: {
          machineId,
          tagId
        }
      }
    }
  ];

  newChart.xAxes = [
    {
      id: xAxisId,
      alignment: "bottom",
      type: "datetime",
      title: { text: "Date", enabled: true }
    }
  ];

  newChart.yAxes = [
    {
      id: yAxisId,
      alignment: "left",
      type: "linear",
      title: { text: tagName ?? "Y Axis", enabled: true },
      tagId: null
    }
  ];

  return newChart;
};

/** Create method for site average series object */
export const createSiteAverage = (
  parentId: string,
  seriesId: string,
  title: string,
  tagId: number,
  siteId: number,
  xAxisId: string,
  yAxisId: string,
  colorIndex: number,
  pointsEnabled: boolean
): LineSeries<ScadaMedianDataSource> => {
  return {
    parentId,
    id: seriesId,
    seriesType: "line",
    title,
    colorIndex,
    xAxisId,
    yAxisId,
    pointsEnabled,
    dataSource: {
      type: "scada-median",
      args: {
        siteId,
        tagId
      }
    }
  };
};

/** Create method for site range series object */
export const createSiteRange = (
  parentId: string,
  seriesId: string,
  title: string,
  tagId: number,
  siteId: number,
  xAxisId: string,
  yAxisId: string,
  colorIndex: number
): RangeSeries<ScadaRangeDataSource> => {
  return {
    parentId,
    id: seriesId,
    dataSource: {
      type: "scada-range",
      args: {
        siteId,
        tagId
      }
    },
    seriesType: "range",
    title,
    colorIndex,
    xAxisId,
    yAxisId
  };
};
